import type { JSX } from "preact";
import type { Operator } from "../api";
import type { Route } from "../lib/route";
import type { ThemeChoice } from "../lib/theme";
import { useI18n } from "../lib/i18n";
import { Avatar } from "./Avatar";
import {
  BuildingIcon,
  ChartIcon,
  HistoryIcon,
  InboxIcon,
  LogoutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  SparkleIcon,
  SunIcon,
  UsersIcon,
} from "./Icons";

export type Presence = "online" | "away";

export type NavKey = "inbox" | "visitors" | "history" | "stats" | "earlyAccess" | "customers" | "settings";

/**
 * Which rail buttons show for this operator.
 *
 * Early access and Customers are platform-admin screens (CHAT_PLATFORM_ADMINS
 * on the server, see routes/platform.ts): everyone else must not see so much
 * as a button for them. Pulled out as a pure function so that rule is testable
 * without rendering the rail.
 */
export function navKeys(operator: { isPlatformAdmin?: boolean }): NavKey[] {
  const keys: NavKey[] = ["inbox", "visitors", "history", "stats"];
  if (operator.isPlatformAdmin) keys.push("earlyAccess", "customers");
  keys.push("settings");
  return keys;
}

/**
 * The console's left rail. Ink in both themes; the one screen element that is
 * always there, so it carries what must always be reachable: where you are,
 * whether you are taking chats, and who you are signed in as.
 */
export function NavRail({
  route,
  go,
  waiting,
  operator,
  presence,
  onPresence,
  theme,
  onTheme,
  onSignOut,
}: {
  route: Route;
  go: (route: Route) => void;
  waiting: number;
  operator: Operator;
  presence: Presence;
  onPresence: (next: Presence) => void;
  theme: ThemeChoice;
  onTheme: () => void;
  onSignOut: () => void;
}): JSX.Element {
  const { t: all } = useI18n();
  const t = all.nav;
  const themeLabel = theme === "light" ? t.themeLight : theme === "dark" ? t.themeDark : t.themeSystem;
  const keys = navKeys(operator);

  return (
    <nav class="rail" aria-label={t.label}>
      <div class="brand" aria-hidden="true" title="AgentlyDesk">
        A
      </div>

      <button
        aria-current={route.view === "inbox" ? "page" : undefined}
        aria-label={waiting > 0 ? t.inboxWaiting(waiting) : t.inbox}
        title={t.inbox}
        onClick={() => go({ view: "inbox", id: route.view === "inbox" ? route.id : null })}
      >
        <InboxIcon />
        {waiting > 0 ? <span class="count">{waiting > 99 ? "99+" : waiting}</span> : null}
      </button>

      <button
        aria-current={route.view === "visitors" ? "page" : undefined}
        aria-label={t.visitors}
        title={t.visitors}
        onClick={() => go({ view: "visitors" })}
      >
        <UsersIcon />
      </button>

      <button
        aria-current={route.view === "history" ? "page" : undefined}
        aria-label={t.history}
        title={t.history}
        onClick={() => go({ view: "history", id: null })}
      >
        <HistoryIcon />
      </button>

      <button
        aria-current={route.view === "stats" ? "page" : undefined}
        aria-label={t.stats}
        title={t.stats}
        onClick={() => go({ view: "stats" })}
      >
        <ChartIcon />
      </button>

      {keys.includes("earlyAccess") ? (
        <button
          aria-current={route.view === "earlyAccess" ? "page" : undefined}
          aria-label={t.earlyAccess}
          title={t.earlyAccess}
          onClick={() => go({ view: "earlyAccess" })}
        >
          <SparkleIcon />
        </button>
      ) : null}

      {keys.includes("customers") ? (
        <button
          aria-current={route.view === "customers" ? "page" : undefined}
          aria-label={t.customers}
          title={t.customers}
          onClick={() => go({ view: "customers" })}
        >
          <BuildingIcon />
        </button>
      ) : null}

      <button
        aria-current={route.view === "settings" ? "page" : undefined}
        aria-label={t.settings}
        title={t.settings}
        onClick={() => go({ view: "settings", tab: route.view === "settings" ? route.tab : "profile" })}
      >
        <SettingsIcon />
      </button>

      <div class="spacer" />

      <button aria-label={themeLabel} title={themeLabel} onClick={onTheme}>
        {theme === "light" ? <SunIcon /> : theme === "dark" ? <MoonIcon /> : <MonitorIcon />}
      </button>

      <button
        aria-label={presence === "online" ? t.presenceOnline : t.presenceAway}
        title={presence === "online" ? t.presenceOnline : t.presenceAway}
        onClick={() => onPresence(presence === "online" ? "away" : "online")}
      >
        <Avatar name={operator.displayName} color={operator.color} url={operator.avatarUrl} size={32} />
        <span class={`presence ${presence}`} aria-hidden="true" />
      </button>

      <button aria-label={t.signOut} title={t.signOut} onClick={onSignOut}>
        <LogoutIcon />
      </button>
    </nav>
  );
}
